import React, { memo } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import css from './DeliveryOptions.module.css';
import {
  NO,
  OWN,
  SHIPPING,
  SHIPPING_PREFERENCE,
  TUTULIST_LOWERCASE,
} from '../../../util/constants';
import { required } from '../../../util/validators';

// Static shipping preference options
const preferenceOptions = [
  { key: TUTULIST_LOWERCASE, label: 'Shipping labels through Tutulist' },
  { key: OWN, label: 'Manage your own shipping' },
];

/**
 * IconRadioButton
 * Reconstructed locally to match the exact SVG structure required by the CSS.
 */
const IconRadioButton = ({ showAsRequired }) => {
  return (
    <svg width="14" height="14" xmlns="http://www.w3.org/2000/svg">
      {/* Outer Circle (Grey or Red) */}
      <circle
        className={showAsRequired ? css.required : css.notChecked}
        cx="5"
        cy="19"
        r="6"
        transform="translate(2 -12)"
        strokeWidth="2"
        fill="none"
        fillRule="evenodd"
      />

      {/* Inner Dot (Color) - Hidden by default via css.checked, shown via CSS when input is checked */}
      <g
        className={classNames(css.checked, css.checkedStyle)}
        transform="translate(2 -12)"
        fill="none"
        fillRule="evenodd"
      >
        <circle strokeWidth="2" cx="5" cy="19" r="6" />
        <circle fill="#FFF" fillRule="nonzero" cx="5" cy="19" r="3" />
      </g>
    </svg>
  );
};

const DeliveryOptions = ({ form, intl, onVerifyShippingPreferences }) => {
  // Custom handler to ensure state updates + side effects run safely
  const handleChange = (event, input) => {
    const value = event.target.value;

    // 1. Update the Radio Button state immediately
    input.onChange(event);

    // 2. Run side effects (batch updates other fields)
    // Using setTimeout ensures the main state update is settled before triggering side effects
    form.batch(() => {
      form.change('shippingType', SHIPPING);
      form.change('threshold', NO);
    });

    if (onVerifyShippingPreferences) {
      onVerifyShippingPreferences(value);
    }
  };

  return (
    <div className={css.shippingOptions}>
      {preferenceOptions.map(({ key, label }) => (
        <Field
          key={key}
          name={SHIPPING_PREFERENCE}
          type="radio"
          value={key}
          validate={required(
            intl.formatMessage({
              id: 'ShippingDetailsForm.shippingPreferenceRequired',
            })
          )}
        >
          {({ input, meta }) => {
            const showAsRequired = meta.touched && meta.error;
            const inputId = `${SHIPPING_PREFERENCE}.${key}`;

            return (
              <span className={css.root}>
                {/* 1. The Input
                   We apply css.input so it gets opacity: 0 and absolute positioning.
                   We hook up our custom handleChange.
                */}
                <input
                  {...input}
                  id={inputId}
                  className={css.input}
                  onChange={e => handleChange(e, input)}
                />

                {/* 2. The Label
                   Must be immediately after input for the CSS sibling selector (+ label) to work.
                */}
                <label htmlFor={inputId} className={css.label}>
                  <span className={css.radioButtonWrapper}>
                    <IconRadioButton showAsRequired={showAsRequired} />
                  </span>
                  <span className={css.text}>{label}</span>
                </label>
              </span>
            );
          }}
        </Field>
      ))}
    </div>
  );
};

export default memo(DeliveryOptions);
